import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shape TEN-04 publishes, imported rather than restated. A field here that the service
// stopped returning is a build failure, not a blank cell.
import type { BrandingTheme } from "@tdelivr/api-client/generated/tenant/tenant-branding.js";

import {
  brandPage,
  brandRows,
  checkedText,
  colourReason,
  colourState,
  fetchNote,
  verdictOf,
  type BrandOrder,
  type BrandRow,
  type ColourVerdict,
} from "../brand.ts";
import { stateForError, stateForList, type ViewState } from "../view-state.ts";
import { PageHeading } from "./PageHeading.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * Brand and domains: the colours this tenant has published, and whether each one can be read.
 *
 * Built to `17 WP16 Tenant Admin.dc.html`, page `brand`, against TEN-04 `GET /v1/themes` — the
 * third of that file's fifteen pages with a listing a browser can call, and the one R-12 named as
 * reachable and left unbuilt. The claim was checked rather than taken: the operation is public,
 * requires `tenant-branding:read`, takes no path or query parameter, and publishes `tenantId`,
 * `key`, `version`, `displayName`, `colors{primary,secondary,accent}`, `assets{logoUrl,iconUrl}`,
 * `createdAt` and `createdBy` on every row.
 *
 * **Half the design's grid has a source and half does not, and the page is named after the half
 * that does not.** Its four sample rows are two colours and two custom domains; TEN-04 publishes
 * three colours per theme and **nothing anywhere on this platform publishes a tenant's domain,
 * its DNS state or its certificate**. So the rows are colours and the absence is said on screen
 * rather than left as a page called "Brand and domains" with no domain on it.
 *
 * **Nothing on this platform checks a brand colour's contrast, and the design's own sentence under
 * the title says something does.** TEN-04's write validates a six-digit hex, an HTTPS asset URL
 * with no credentials and a display name's length; `nextTheme()` validates only the version. There
 * is no contrast check in the route, the service or the domain. Shipping "Colour choices are
 * checked for contrast before they go live" to an administrator would be a false safety claim about
 * the one thing this page is for, so the sentence says where the check actually happens — here,
 * when they look at it — and Q-35 records that the words are written rather than read. The check
 * itself is the one design rule 6 already requires of every console.
 *
 * R-16 in `docs/design/DESIGN_RECONCILIATION.md` is the whole account.
 */

const brandingApi = services.tenant__tenant_branding;

/** The design's own rows-per-page choices, in its order. */
const PER_PAGE_CHOICES = [5, 10, 25, 50] as const;

/** The design's sort options, in its order and its words. */
const ORDER_CHOICES: readonly { readonly value: BrandOrder; readonly label: string }[] = [
  { value: "default", label: "Default order" },
  { value: "state", label: "State" },
  { value: "az", label: "Name A–Z" },
  { value: "za", label: "Name Z–A" },
];

/**
 * The verdicts the toolbar's filter offers.
 *
 * The words are the badges', not the verdict names, because the badge is what an administrator is
 * looking at. Computed here rather than refetched: TEN-04's listing takes no query parameter at
 * all, so there is nothing to narrow it with.
 */
const VERDICT_CHOICES: readonly { readonly value: ColourVerdict; readonly label: string }[] = [
  { value: "safe", label: "live" },
  { value: "fill_only", label: "auto-adjusted" },
  { value: "replaced", label: "replaced" },
];

/** The export formats the design's menu offers, in its order and its words. */
const EXPORTS = [
  { format: "csv", glyph: "description", label: "CSV" },
  { format: "xls", glyph: "table_view", label: "Excel (.xls)" },
  { format: "pdf", glyph: "picture_as_pdf", label: "PDF" },
] as const;

type ExportFormat = (typeof EXPORTS)[number]["format"];

export interface BrandDomainsProps {
  readonly transport: Transport;
  /** Innermost last: tenant, group, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
}

export function BrandDomains({ transport, crumbs }: BrandDomainsProps): ReactNode {
  const [themes, setThemes] = useState<readonly BrandingTheme[] | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [verdict, setVerdict] = useState<ColourVerdict | undefined>(undefined);
  const [order, setOrder] = useState<BrandOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRegion = useRef<HTMLSpanElement>(null);

  const load = useCallback(async () => {
    const client = brandingApi.createTenantBrandingClient(transport);
    try {
      const answered = await client.getThemes();
      setThemes(answered.themes);
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, ["tenant-branding:read"]));
      setThemes(undefined);
    }
  }, [transport]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!exportOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setExportOpen(false);
    };
    const onPointer = (event: MouseEvent): void => {
      const region = exportRegion.current;
      if (region !== null && !region.contains(event.target as Node)) setExportOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [exportOpen]);

  const rows = useMemo(() => brandRows(themes ?? []), [themes]);
  const view = useMemo(
    () =>
      brandPage(rows, {
        search,
        order,
        page,
        perPage,
        ...(verdict === undefined ? {} : { verdict }),
      }),
    [rows, search, order, page, perPage, verdict],
  );

  /*
   * No `asOf`, so no stale state — the fifth screen to need that field.
   *
   * TEN-04's listing publishes `createdAt` per theme, which is when that version was written, and
   * no page-level instant. A branding board is not a live board, so this costs less here than it
   * does on the live map; it is recorded all the same, because the recommendation is the same one
   * and it is one field.
   */
  /*
   * Decided from `themes`, not from `rows`.
   *
   * `brandRows([])` is an empty array, and `stateForList` reads an empty array as the design's
   * *empty* state — so passing the rows made the board say "Nothing here yet" while the request was
   * still in flight, and the designed loading state was unreachable. `themes` is `undefined` until
   * TEN-04 answers, which is the distinction the six states are built on. Caught by
   * `tests/screens-render.test.tsx` on its first run rather than by reading.
   */
  const viewState =
    failure ??
    stateForList(themes === undefined ? undefined : rows, { filtered: verdict !== undefined });

  const clearFilters = (): void => {
    setSearch("");
    setVerdict(undefined);
    setPage(1);
  };

  const note = fetchNote(themes?.length);

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="records__head">
      <span>Item</span>
      <span>Value</span>
      <span>Checked</span>
      <span className="records__head--right">State</span>
    </div>
  );

  return (
    <section className="page records">
      <PageHeading
        crumbs={crumbs ?? []}
        title="Brand and domains"
        /*
         * `TEN-04`, where the design's chip reads `TEN-03 · TEN-04`.
         *
         * TEN-03 is the entitlement service, and its one read answers a single entitlement for a
         * `capability` name the page would have to already know — so the design's `Plan · Growth`
         * tile has no source and TEN-03 reads nothing this page draws. R-12 measured it and the
         * re-test in R-16 confirmed it: the capability path parameter is `type: string` with no
         * enum, so there is no vocabulary to enumerate.
         */
        source="TEN-04"
        /*
         * The design's sentence, with its second clause replaced by a measured one.
         *
         * The design writes "Logo, colours and the web address customers see. Colour choices are
         * checked for contrast before they go live." Nothing on this platform checks contrast at
         * any point, so the second clause is a safety claim about the one thing this page exists
         * for and it is false. Q-35 records the replacement; R-16 records the measurement.
         */
        description="Logo, colours and the web address customers see. Contrast is checked here, not when a colour is published."
        action={
          <button
            type="button"
            className="records__primary"
            /*
             * Drawn, and refused — and the label is the reason.
             *
             * The design's form is `Edit brand`, its note reads "Changes are saved as a draft
             * version. Nothing changes for customers until you publish.", and its submit says
             * "Save draft". **TEN-04 has no draft and no publish step.** `PUT /v1/themes/{key}`
             * writes an immutable version that `GET /v1/themes/{key}` returns immediately, because
             * that read is `ORDER BY version DESC LIMIT 1`. A button labelled "Save draft" over
             * that operation would tell an administrator nothing had reached their customers at the
             * moment it had.
             *
             * Four of the form's six fields are also unwritable or absent: `Logo` is a file drop
             * and `assets.logoUrl` is a URL with no upload operation anywhere on TEN-04;
             * `Custom domain` and `Support email` have no field on the write at all; and
             * `colors.secondary` is **required** by the body schema and the form has no field for
             * it, so the form as designed could not satisfy the contract even for the fields it
             * does have.
             */
            disabled
            title="TEN-04 has no draft: PUT /v1/themes/{key} publishes a new version immediately, and the design's form says nothing changes until you publish. Its Logo field is a file drop where the contract takes a URL, its domain and support-email fields have no field on the write, and the required `colors.secondary` has no field on the form. See R-16."
          >
            <span aria-hidden="true" className="records__primary-glyph">
              add
            </span>
            Edit brand
          </button>
        }
      />

      {/*
        No tiles, where the design has four — and on this page one of them was nearly buildable,
        which is worth saying.

        `Brand version · v6` is one number over a tenant that may hold several themes, each with its
        own version. `Draft changes · 1` counts something TEN-04 does not have. `Domains · 2` counts
        something nothing publishes. And `Contrast checks · 9 of 9` is the one this console could
        compute, because it computes exactly that for every row below — but it would be 9 of the
        rows the listing happened to answer, and `fetchNote` is why that is not a total: TEN-04's
        listing publishes no limit and no cursor and the service caps it at 200 theme keys, so the
        denominator is not merely a floor, it is a number the page cannot know the standing of.
        R-16.
      */}

      <div className="records__panel">
        <div className="records__toolbar">
          <span className="records__search">
            <span aria-hidden="true" className="records__search-glyph">
              search
            </span>
            <input
              id="brand-search"
              type="search"
              aria-label="Search"
              placeholder="Search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </span>
          <select
            id="brand-verdict"
            aria-label="Filter by state"
            value={verdict ?? ""}
            onChange={(event) => {
              setVerdict(
                event.target.value === "" ? undefined : (event.target.value as ColourVerdict),
              );
              setPage(1);
            }}
          >
            <option value="">All states</option>
            {VERDICT_CHOICES.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
          <select
            id="brand-order"
            aria-label="Sort"
            value={order}
            onChange={(event) => {
              setOrder(event.target.value as BrandOrder);
            }}
          >
            {ORDER_CHOICES.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
          <span className="records__export" ref={exportRegion}>
            <button
              type="button"
              className="records__export-button"
              aria-expanded={exportOpen}
              onClick={() => {
                setExportOpen((open) => !open);
              }}
            >
              <span aria-hidden="true" className="records__export-glyph">
                download
              </span>
              Export
            </button>
            {exportOpen ? (
              <div className="records__export-menu" role="menu">
                {EXPORTS.map((choice) => (
                  <button
                    key={choice.format}
                    type="button"
                    role="menuitem"
                    className="records__export-item"
                    onClick={() => {
                      setExportOpen(false);
                      exportRows(view.all, choice.format);
                    }}
                  >
                    <span aria-hidden="true" className="records__export-item-glyph">
                      {choice.glyph}
                    </span>
                    {choice.label}
                  </button>
                ))}
              </div>
            ) : null}
          </span>
        </div>

        <StateView
          state={viewState}
          purpose="Colours appear here as soon as a theme is published for this tenant."
          onRetry={() => {
            void load();
          }}
          skeleton={{
            head,
            columns: 4,
            rowClassName: "records__row",
            // Both strings come from the trail the shell already draws, so the
            // sentence can never disagree with the breadcrumb above it.
            page: crumbs?.at(-1) ?? "this page",
            tenant: crumbs?.[0] ?? "this tenant",
          }}
        >
          {head}
          <div className="records__rows">
            {view.rows.map((row) => {
              const shown = colourState(row);
              return (
                <div
                  key={row.id}
                  className={
                    verdictOf(row) === "safe" ? "records__row" : "records__row records__row--cross"
                  }
                  title={`${row.theme.displayName} · ${row.theme.key} · v${String(row.theme.version)} · published ${row.theme.createdAt} by ${row.theme.createdBy}`}
                >
                  <span className="records__when">{row.item}</span>
                  <span className="records__actor records__swatch-cell">
                    {/*
                      The colour as it will be rendered, beside the hex as published.

                      Not an addition: the design's `Value` cell for a colour row is the hex, and a
                      hex an administrator cannot see is a hex they cannot judge. The swatch is 10px
                      — the badge glyph's own size from `glyph()` — and it is never the only signal:
                      the hex is beside it and the verdict is in the next column, which is design
                      rule 2's own rule about colour restated.

                      The swatch is the **resolved** background and the text is what TEN-04
                      published. On a well-formed colour those are the same value; on one the
                      resolver replaced they are not, and showing the published hex beside the
                      platform colour an administrator will actually see is the point of the pair.
                    */}
                    <span
                      aria-hidden="true"
                      className="records__swatch"
                      style={{ background: row.resolved.background }}
                    />
                    <code className="records__hex">{row.value}</code>
                  </span>
                  <span className="records__action">{checkedText(row)}</span>
                  <span className="records__state">
                    <StateBadge
                      code="TEN-04"
                      status={verdictOf(row)}
                      classKey={shown.classKey}
                      label={shown.label}
                      title={colourReason(row)}
                    />
                  </span>
                </div>
              );
            })}
          </div>
          {view.total === 0 ? (
            <div className="records__no-match">
              <span aria-hidden="true" className="records__no-match-glyph">
                search_off
              </span>
              Nothing matches this search or filter.
              <button type="button" className="records__clear" onClick={clearFilters}>
                Clear filters
              </button>
            </div>
          ) : null}
          <div className="records__pager">
            <span>
              {view.total === 0
                ? "No matching rows"
                : `Showing ${(page - 1) * perPage + 1}–${Math.min(page * perPage, view.total)} of ${view.total}`}
            </span>
            <span className="records__pager-controls">
              <select
                id="brand-per-page"
                aria-label="Rows per page"
                value={perPage}
                onChange={(event) => {
                  setPerPage(Number(event.target.value));
                  setPage(1);
                }}
              >
                {PER_PAGE_CHOICES.map((choice) => (
                  <option key={choice} value={choice}>
                    {choice} per page
                  </option>
                ))}
              </select>
              <button
                type="button"
                aria-label="Previous page"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                <span aria-hidden="true">chevron_left</span>
              </button>
              <span className="records__page">
                Page {Math.min(page, view.pageCount)} of {view.pageCount}
              </span>
              <button
                type="button"
                aria-label="Next page"
                disabled={page >= view.pageCount}
                onClick={() => setPage((current) => Math.min(view.pageCount, current + 1))}
              >
                <span aria-hidden="true">chevron_right</span>
              </button>
            </span>
          </div>
        </StateView>

        {/*
          The pager pages what is held, and this line says what being held means.

          Unlike the audit board, the paging here is honest about the rows in hand: TEN-04 answers
          everything in one request and the pager walks it. What it cannot be honest about is
          whether those rows are all of them — the listing publishes no limit, no cursor and no
          total, and the service caps its own query at 200 theme keys. So there is no signal to page
          on and no signal to stop at. R-13's precedent is followed: say it on screen rather than
          imply completeness. Always present, because the fact is always true.
        */}
        {note === undefined ? null : (
          <p className="records__bounded" role="status">
            {note}
          </p>
        )}
      </div>

      {/*
        The closing callout, in the design's own `info` style and its own words.

        Kept word for word, unlike the zones board's, because this one is achievable: every PUT
        writes an immutable version, `GET /v1/themes/{key}/versions/{version}` reads any earlier
        one, and republishing an earlier version's values restores it. What has no operation is
        making an earlier version current *in place* — the restore is a new, higher version — and
        that is a mechanism rather than a contradiction of the sentence, so it is recorded in R-16
        and not written on the page.
      */}
      <div className="records__note records__note--info">
        <span aria-hidden="true" className="records__note-glyph">
          info
        </span>
        <span>
          Publishing a brand change creates a new version; you can roll back to any earlier one.
        </span>
      </div>
    </section>
  );
}

/**
 * Export what the administrator is looking at, in the columns they are looking at.
 *
 * The filtered and ordered set rather than the page, and the words on screen rather than the
 * verdict names — so an exported row says the same thing the board did.
 */
function exportRows(rows: readonly BrandRow[], format: ExportFormat): void {
  const header = ["Item", "Value", "Checked", "State"];
  const body = rows.map((row) => [row.item, row.value, checkedText(row), colourState(row).label]);

  if (format === "csv") {
    const quote = (value: string): string => `"${value.replaceAll('"', '""')}"`;
    const csv = [header, ...body].map((line) => line.map(quote).join(",")).join("\n");
    download(new Blob([csv], { type: "text/csv" }), "brand.csv");
    return;
  }

  const table =
    '<table border="1" cellpadding="6" style="border-collapse:collapse;font-family:sans-serif;font-size:12px">' +
    `<tr>${header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>` +
    body
      .map((line) => `<tr>${line.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
      .join("") +
    "</table>";

  if (format === "xls") {
    download(
      new Blob([`<html><head><meta charset="utf-8"></head><body>${table}</body></html>`], {
        type: "application/vnd.ms-excel",
      }),
      "brand.xls",
    );
    return;
  }

  const printable = window.open("", "_blank");
  if (printable === null) return;
  printable.document.write(
    `<html><head><title>brand</title></head><body><h2 style="font-family:sans-serif">brand</h2>${table}</body></html>`,
  );
  printable.document.close();
  printable.focus();
  printable.print();
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function download(blob: Blob, name: string): void {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.append(link);
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    link.remove();
  }, 500);
}
